import { Box, Tooltip, Typography } from '@mui/material';
import { ApexOptions } from 'apexcharts';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';
import { TagsHistogramEntry, TagsHistogramInterval } from '../../../api/hexa/models/EntityStatisticsModels';
import { sgrColors } from '../../../ui/sgr/SgrColors';

export const CATEGORICAL_COLORS = [
  sgrColors.navy, // brand navy
  '#eb6834', // orange
  '#1baf7a', // aqua
  '#eda100', // yellow
  '#e87ba4', // magenta
  '#008300', // green
  '#4a3aa7', // violet
  '#e34948', // red
];

const OTHER_COLOR = sgrColors.backdrop;
/** Each plotted tag needs its own color, so the palette bounds the series and the legend */
export const MAX_PLOTTED_TAGS = CATEGORICAL_COLORS.length;
/** "Other" can group many tags, so its tooltip lists only the busiest ones */
const MAX_OTHER_TOOLTIP_TAGS = 15;

const DATE_FORMATS: Record<TagsHistogramInterval, string> = {
  day: 'DD/MM/YYYY',
  month: 'MM/YYYY',
  year: 'YYYY',
};

interface TagsHistogramChartProps {
  histogram: TagsHistogramEntry[];
  /** Tags plotted as their own series, in color order; the rest are summed into "Other" */
  plottedTags: string[];
  interval: TagsHistogramInterval;
}

const TagsHistogramChart = ({
  histogram,
  plottedTags,
  interval,
}: TagsHistogramChartProps) => {
  const { t } = useTranslation();

  // Series hidden from the legend; they keep their slot so the colors don't shift
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());

  const { series, colors, overflow } = useMemo(() => {
    const entriesByTag = new Map(histogram.map((entry) => [entry.tag, entry]));
    const shown = plottedTags
      .slice(0, MAX_PLOTTED_TAGS)
      .flatMap((tag) => entriesByTag.get(tag) ?? []);
    const shownTags = new Set(shown.map((entry) => entry.tag));
    const overflow = histogram
      .filter((entry) => !shownTags.has(entry.tag))
      .sort((a, b) => b.total - a.total);

    const series = shown.map((entry) => ({
      name: entry.tag,
      data: entry.buckets.map((bucket) => ({
        x: dayjs(bucket.date).valueOf(),
        y: bucket.count,
      })),
    }));
    const colors = shown.map((_, index) => CATEGORICAL_COLORS[index]);

    if (overflow.length > 0) {
      const otherByDate = new Map<number, number>();
      overflow.forEach((entry) => {
        entry.buckets.forEach((bucket) => {
          const x = dayjs(bucket.date).valueOf();
          otherByDate.set(x, (otherByDate.get(x) ?? 0) + bucket.count);
        });
      });
      series.push({
        name: t('entity_statistics.tags_histogram_other'),
        data: Array.from(otherByDate.entries())
          .sort(([a], [b]) => a - b)
          .map(([x, y]) => ({ x, y })),
      });
      colors.push(OTHER_COLOR);
    }

    return { series, colors, overflow };
  }, [histogram, plottedTags, t]);

  const visibleSeries = series.map((entry) =>
    hiddenSeries.has(entry.name) ? { ...entry, data: [] } : entry
  );

  const toggleSeries = (name: string) =>
    setHiddenSeries((previous) => {
      const next = new Set(previous);
      if (!next.delete(name)) next.add(name);
      return next;
    });

  const otherTooltip = (
    <Box sx={{ padding: '0.25rem' }}>
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
        {t('entity_statistics.tags_histogram_other_tooltip', {
          count: overflow.length,
        })}
      </Typography>
      <Box
        component="ul"
        sx={{ margin: '0.25rem 0 0', paddingLeft: '1rem' }}
      >
        {overflow.slice(0, MAX_OTHER_TOOLTIP_TAGS).map((entry) => (
          <Typography component="li" variant="caption" key={entry.tag}>
            {entry.tag} ({entry.total.toLocaleString()})
          </Typography>
        ))}
      </Box>
      {overflow.length > MAX_OTHER_TOOLTIP_TAGS && (
        <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
          {t('entity_statistics.tags_histogram_other_tooltip_more', {
            count: overflow.length - MAX_OTHER_TOOLTIP_TAGS,
          })}
        </Typography>
      )}
    </Box>
  );

  const options: ApexOptions = {
    chart: {
      id: 'tagsHistogramChart',
      type: 'line',
      toolbar: { show: false },
      zoom: { enabled: false },
      fontFamily: 'sgr-albert-sans, sans-serif',
      background: 'transparent',
      animations: { enabled: false },
    },
    colors,
    stroke: {
      width: 2,
      curve: 'straight',
    },
    markers: {
      size: 4,
      strokeWidth: 2,
      strokeColors: sgrColors.frost_grey,
      hover: { sizeOffset: 2 },
    },
    grid: {
      borderColor: sgrColors.grey_cloud,
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      padding: { left: 12, right: 16, top: 0, bottom: 0 },
    },
    // Rendered below as a React legend, so "Other" can show which tags it groups
    legend: { show: false },
    dataLabels: { enabled: false },
    noData: {
      text: t('entity_statistics.tags_histogram_empty'),
      align: 'center',
      verticalAlign: 'middle',
      style: {
        color: sgrColors.backdrop,
        fontSize: '1.5rem',
      },
    },
    xaxis: {
      type: 'datetime',
      axisBorder: { color: sgrColors.grey_cloud },
      axisTicks: { color: sgrColors.grey_cloud },
      tooltip: { enabled: false },
      labels: {
        datetimeUTC: false,
        style: { colors: sgrColors.backdrop, fontSize: '0.75rem' },
        formatter: (value) => dayjs(Number(value)).format(DATE_FORMATS[interval]),
      },
    },
    yaxis: {
      labels: {
        style: { colors: sgrColors.backdrop, fontSize: '0.75rem' },
        formatter: (value) => Math.round(value).toLocaleString(),
      },
    },
    tooltip: {
      shared: true,
      followCursor: true,
      x: {
        formatter: (value) => dayjs(Number(value)).format(DATE_FORMATS[interval]),
      },
      marker: {
        show: false,
      }
    },
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          rowGap: '0.25rem',
          columnGap: '1rem',
          padding: '0 0.5rem',
        }}
      >
        {series.map((entry, index) => {
          // "Other" is always appended last, and only when some tags aren't plotted
          const isOther = overflow.length > 0 && index === series.length - 1;
          const isHidden = hiddenSeries.has(entry.name);
          const item = (
            <Box
              key={entry.name}
              component="button"
              type="button"
              onClick={() => toggleSeries(entry.name)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                padding: 0,
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontFamily: 'sgr-albert-sans, sans-serif',
                fontSize: '13px',
                color: sgrColors.mine_shaft,
                opacity: isHidden ? 0.4 : 1,
              }}
            >
              <Box
                component="span"
                sx={{
                  width: '0.5rem',
                  height: '0.5rem',
                  borderRadius: '50%',
                  flexShrink: 0,
                  backgroundColor: colors[index],
                }}
              />
              {entry.name}
            </Box>
          );
          return isOther ? (
            <Tooltip key={entry.name} title={otherTooltip} arrow>
              {item}
            </Tooltip>
          ) : (
            item
          );
        })}
      </Box>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ReactApexChart
          height="100%"
          width="100%"
          options={options}
          series={visibleSeries}
          type="line"
        />
      </Box>
    </Box>
  );
};

export default TagsHistogramChart;
